import React from "react";
import useCurrentVehicleData from "../hooks/useCurrentVehicleData";
import useVersionsData from "../hooks/useVersionsData";
import { FUEL_LABELS, formatDecimal, priceUnitShort, priceUnitLong } from "../utils/format";
import Tooltip from "./Tooltip";

const FUEL_OPTIONS = ["GASOLINE", "DIESEL", "ELECTRIC"];

const Step2CurrentVehicleForm = () => {
    const {
        getFuelType,
        getEfficiency,
        getAnnualKm,
        getReferencePrice,
        getEfficiencyUnit,
        getPriceUnit,
        setFuelType,
        setEfficiency,
        setAnnualKm,
    } = useCurrentVehicleData();
    const { getSelectedIds } = useVersionsData();

    const fuelType = getFuelType();
    // Sin modelo elegido no hay nada que calcular: el paso se muestra atenuado.
    const disabled = getSelectedIds().length === 0;

    // El rendimiento admite solo 1 o 2 dígitos enteros (0-99), con un decimal opcional.
    const handleEfficiencyChange = (value) => {
        if (value === "" || /^\d{1,2}([.,]\d?)?$/.test(value)) {
            setEfficiency(value);
        }
    };

    return (
        <section
            className={
                "savings-calculator__card savings-calculator__step" +
                (disabled ? " is-disabled" : "")
            }
            aria-disabled={disabled}
        >
            <div className="savings-calculator__step-badge">2</div>
            <h3 className="savings-calculator__step-title">
                Los datos de tu vehículo actual
            </h3>

            {/* Tipo de combustible */}
            <div className="savings-calculator__field">
                <label className="savings-calculator__label">
                    Combustible de tu vehículo actual
                </label>
                <div className="savings-calculator__toggle" role="group">
                    {FUEL_OPTIONS.map((option) => (
                        <button
                            key={option}
                            type="button"
                            className={
                                "savings-calculator__toggle-btn" +
                                (fuelType === option ? " is-active" : "")
                            }
                            onClick={() => setFuelType(option)}
                            disabled={disabled}
                        >
                            {FUEL_LABELS[option] === "Electricidad"
                                ? "Eléctrico"
                                : FUEL_LABELS[option]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Rendimiento actual */}
            <div className="savings-calculator__field">
                <label className="savings-calculator__label">
                    Rendimiento actual{" "}
                    <span className="tag">EDITABLE</span>{" "}
                    <Tooltip text="En promedio, los modelos a bencina rinden entre 10 y 15 km/l, y las camionetas diésel entre 11 y 13 km/l. Los eléctricos rinden en promedio entre 5 y 10 km/kWh." />
                </label>
                <div className="savings-calculator__input-wrap">
                    <input
                        type="number"
                        min="0"
                        max="99"
                        step="0.1"
                        inputMode="decimal"
                        className="savings-calculator__input"
                        value={getEfficiency()}
                        placeholder="0"
                        disabled={disabled}
                        onKeyDown={(e) => {
                            if (["e", "E", "+", "-"].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onChange={(e) => handleEfficiencyChange(e.target.value)}
                    />
                    <span className="savings-calculator__input-unit">
                        {getEfficiencyUnit()}
                    </span>
                </div>
                <p className="savings-calculator__help">
                    ¿Cuántos km recorre tu auto por {priceUnitLong(fuelType)}?
                </p>
            </div>

            {/* Precio del combustible (solo referencia) */}
            <div className="savings-calculator__field">
                <label className="savings-calculator__label">
                    Precio del combustible{" "}
                    <Tooltip text="Autocompletado con un valor de referencia. Puedes editarlo si conoces el precio exacto que pagas." />
                </label>
                <div className="savings-calculator__input-wrap is-readonly">
                    <input
                        type="text"
                        className="savings-calculator__input"
                        value={formatDecimal(getReferencePrice(), 0)}
                        readOnly
                        tabIndex={-1}
                    />
                    <span className="savings-calculator__input-unit">{getPriceUnit()}</span>
                </div>
                <p className="savings-calculator__help">
                    Precio referencial.
                </p>
            </div>

            {/* Kilómetros al año */}
            <div className="savings-calculator__field">
                <label className="savings-calculator__label">
                    Kilómetros al año{" "}
                    <Tooltip text="El promedio de uso anual en Chile es de aprox. 15.000 km." />
                </label>
                <div className="savings-calculator__input-wrap">
                    <input
                        type="number"
                        min="0"
                        step="500"
                        className="savings-calculator__input"
                        value={getAnnualKm()}
                        placeholder="0"
                        disabled={disabled}
                        onChange={(e) => setAnnualKm(e.target.value)}
                    />
                    <span className="savings-calculator__input-unit">km</span>
                </div>
                <p className="savings-calculator__help">
                    Promedio anual en Chile: ~15.000 km.
                </p>
            </div>
        </section>
    );
};

export default Step2CurrentVehicleForm;
